/**
 * Inline `$…$` math, with markdown-it-katex's currency rule.
 *
 * `micromark-extension-llm-math`'s `singleDollarTextMath` is all-or-nothing:
 * turn it on and `plus $5 and $10` becomes a math span. Agents in this product
 * write far more currency than inline math, so that option stays off and this
 * construct takes the single-dollar case instead, applying the delimiter test
 * markdown-it-katex uses — the same one `forest/markdown-editor/math.ts` ports
 * for Tiptap, so a doc and a chat message agree on what is math:
 *
 *   - the opening `$` is not followed by whitespace (nor by another `$`, which
 *     is the stock construct's `$$…$$`);
 *   - the closing `$` is not preceded by whitespace and not followed by a
 *     digit, so `$1.10 = $110.00` stays two prices;
 *   - `\$` inside a span is an escape, not a closing delimiter;
 *   - a span stays on one line.
 *
 * Like markdown-it, a failed closing test abandons the whole span rather than
 * scanning on for a later `$`, and an abandoned `$` stays literal text.
 *
 * The tokens are the stock ones (`mathText` / `mathTextSequence` /
 * `mathTextData`), so `mdast-util-math` turns them into the same `inlineMath`
 * node the bracket and double-dollar forms produce.
 */

// Side-effect type import for the token types the fork declares on
// `micromark-util-types`; we emit those same tokens.
import type {} from "micromark-extension-llm-math";
import {
  asciiDigit,
  markdownLineEnding,
  markdownSpace,
} from "micromark-util-character";
import { codes, types } from "micromark-util-symbol";
import type {
  Code,
  Construct,
  Effects,
  Extension,
  State,
  TokenizeContext,
} from "micromark-util-types";

function tokenizeDollarMath(
  this: TokenizeContext,
  effects: Effects,
  ok: State,
  nok: State,
): State {
  /** The last code consumed inside the span, for the closing test. */
  let last: Code = codes.eof;

  return start;

  function start(code: Code): State | undefined {
    effects.enter("mathText");
    effects.enter("mathTextSequence");
    effects.consume(code);
    effects.exit("mathTextSequence");
    return afterOpen;
  }

  function afterOpen(code: Code): State | undefined {
    if (
      code === codes.dollarSign ||
      code === codes.eof ||
      markdownSpace(code) ||
      markdownLineEnding(code)
    ) {
      return nok(code);
    }
    effects.enter("mathTextData");
    return data(code);
  }

  function data(code: Code): State | undefined {
    if (code === codes.eof || markdownLineEnding(code)) return nok(code);

    if (code === codes.dollarSign) {
      if (markdownSpace(last)) return nok(code);
      effects.exit("mathTextData");
      effects.enter("mathTextSequence");
      effects.consume(code);
      effects.exit("mathTextSequence");
      return afterClose;
    }

    const backslash = code === codes.backslash;
    effects.consume(code);
    last = code;
    return backslash ? escape : data;
  }

  /** The code after a backslash is content, whatever it is. */
  function escape(code: Code): State | undefined {
    if (code === codes.eof || markdownLineEnding(code)) return nok(code);
    effects.consume(code);
    last = code;
    return data;
  }

  function afterClose(code: Code): State | undefined {
    if (asciiDigit(code)) return nok(code);
    effects.exit("mathText");
    return ok(code);
  }
}

/** A `$` opens a span unless it follows an unescaped `$`. */
function previousIsNotDollar(this: TokenizeContext, code: Code): boolean {
  if (code !== codes.dollarSign) return true;
  const tail = this.events[this.events.length - 1];
  return tail?.[1].type === types.characterEscape;
}

const dollarMathText: Construct = {
  name: "dollarMathText",
  tokenize: tokenizeDollarMath,
  previous: previousIsNotDollar,
  // Runs after the stock construct, which keeps `$$…$$` (and the bracket
  // forms) parsing there and leaves this only the single-dollar leftovers.
  add: "after",
};

export const dollarMath: Extension = {
  text: { [codes.dollarSign]: dollarMathText },
};
